/**
 * Menu que cresce com a loja (docs/requisito-onboarding-painel-v1.md, seção 5). Arquivo puro, sem dependências:
 * o servidor usa para registrar as novidades de cada fase e o painel (menu-v2) importa para filtrar o catálogo.
 * Ids = ids do catálogo do menu-v2 (a rota sem /admin/).
 */
export type FaseDoMenu = 1 | 2 | 3 | 4;

export const NOMES_DAS_FASES: Record<FaseDoMenu, string> = {1: 'Montando', 2: 'Pronta', 3: 'Vendendo', 4: 'Crescendo'};

/** Quando cada leva chega, do jeito que o dono entende. */
export const QUANDO_CHEGA: Record<FaseDoMenu, string> = {
  1: 'Agora',
  2: 'Depois do pedido de teste',
  3: 'Quando chegar o 1º pedido de verdade',
  4: 'Com 10 pedidos ou 7 dias vendendo'
};

/** Fase 1 · Montando: só o que monta a loja. Pedidos entra quando o dono escolhe onde recebe. */
export const FUNCOES_FASE_1 = ['comecar', 'produtos-v2', 'produtos-v2/importar-ia', 'loja', 'loja/tab/horarios', 'loja/formaspagamento/offline'];

export const FUNCOES_POR_FASE: Record<2 | 3 | 4, string[]> = {
  2: ['index', 'pedidos', 'pedidos/novo-v3', 'pdv', 'adicionais-v2', 'produtos-v2/novo', 'loja/tab/impressao', 'fila-impressao',
    'loja/tab/fotos', 'temas', 'loja/tab/pausasprogramadas', 'loja/numeros-whatsapp', 'loja/tab/usuarios', 'estoque', 'estoque/alertas'],
  3: ['contatos', 'contatos/novo', 'notificacoes', 'avaliacoes', 'mensagens', 'financeiro-delivery'],
  4: ['relatorios', 'insights2', 'fidelidade/pontuacoes', 'loja/tab/fidelidade', 'marketing', 'nova-campanha', 'cupons', 'promocoes',
    'entregadores', 'pedidos/analise', 'contatos-kanban', 'contatos-fila', 'financeiro-balcao', 'taxas', 'banner', 'vitrines', 'tempo-real']
};

/** Uma linha por função, para o Descobrir e o cartão de novidade. */
export const DESCRICOES_DAS_FUNCOES: Record<string, string> = {
  'index': 'O resumo do dia da sua loja',
  'pedidos': 'Os pedidos chegam aqui com som',
  'pedidos/novo-v3': 'Lance o pedido que chegou por telefone ou no balcão',
  'pdv': 'Venda no balcão, direto no caixa',
  'adicionais-v2': 'Complementos, sabores e extras dos produtos',
  'produtos-v2/novo': 'Cadastre um produto novo',
  'loja/tab/impressao': 'Impressora e como o pedido sai no papel',
  'fila-impressao': 'O que está esperando para imprimir',
  'loja/tab/fotos': 'Logo, capa e fotos da loja',
  'temas': 'Cores e cara da sua loja',
  'loja/tab/pausasprogramadas': 'Feche a loja num horário ou feriado',
  'loja/numeros-whatsapp': 'Os números de WhatsApp da loja',
  'loja/tab/usuarios': 'Acesso para o atendente e a equipe, cada um com sua senha',
  'estoque': 'Controle o que entra e sai do estoque',
  'estoque/alertas': 'O que está acabando e precisa repor',
  'contatos': 'Todo mundo que pediu fica salvo aqui, com o histórico',
  'contatos/novo': 'Cadastre um cliente',
  'notificacoes': 'O que o cliente recebe no WhatsApp a cada passo do pedido',
  'avaliacoes': 'A nota que o cliente dá depois de receber',
  'mensagens': 'Tudo o que foi enviado aos clientes',
  'financeiro-delivery': 'Quanto você vendeu no delivery',
  'relatorios': 'O que mais vende, dia forte e ticket médio',
  'insights2': 'Quais produtos chamam atenção e quais vendem',
  'fidelidade/pontuacoes': 'Cartão de pontos para o cliente voltar',
  'loja/tab/fidelidade': 'Regras do seu programa de fidelidade',
  'marketing': 'Chame de volta quem sumiu',
  'nova-campanha': 'Mande uma mensagem para seus clientes',
  'cupons': 'Desconto para trazer cliente novo',
  'promocoes': 'Ofertas que aparecem na loja',
  'entregadores': 'Quem entregou o quê e quanto pagar a cada um',
  'pedidos/analise': 'Os pedidos no mapa',
  'contatos-kanban': 'Clientes por segmento: fiéis, esfriando, sumidos',
  'contatos-fila': 'Quem chamar hoje',
  'financeiro-balcao': 'Quanto você vendeu no balcão',
  'taxas': 'As taxas de entrega que o sistema calculou',
  'banner': 'Banners no topo do cardápio',
  'vitrines': 'Destaques do cardápio',
  'tempo-real': 'Quem está olhando o cardápio agora'
};

export interface EstadoDoMenuOnboarding {
  ativo: boolean;
  fase: number;
  menuCompleto: boolean;
  receberFeito: boolean;
  /** abertas pelo Descobrir ou pela busca antes da fase */
  reveladas?: string[];
}

/** Fase da função no crescimento do menu; 0 = não entra em fase nenhuma (fica no Descobrir e na busca). */
export function faseDaFuncao(id: string): number {
  if (FUNCOES_FASE_1.includes(id)) return 1;
  for (const fase of [2, 3, 4] as const) if (FUNCOES_POR_FASE[fase].includes(id)) return fase;
  return 0;
}

/** A função aparece na navegação agora? (a busca acha todas, sempre) */
export function funcaoVisivelNoMenu(id: string, estado: EstadoDoMenuOnboarding): boolean {
  if (id === 'comecar') return !!estado?.ativo;
  if (!estado || estado.menuCompleto) return true;
  if ((estado.reveladas || []).includes(id)) return true;
  if (id === 'pedidos' && estado.receberFeito) return true;
  const fase = faseDaFuncao(id);
  if (!fase) return false;
  return fase <= Math.max(1, Number(estado.fase || 1));
}

/** Funções que a fase nova traz, na ordem do menu. */
export function funcoesQueChegamNaFase(fase: number): string[] {
  return fase === 2 || fase === 3 || fase === 4 ? FUNCOES_POR_FASE[fase].slice() : [];
}

/**
 * Fase a partir dos acontecimentos reais. Nunca regride e só passa de 1 depois do onboarding concluído.
 * 3 = 1º pedido real; 4 = 10 pedidos reais ou 7 dias desde o primeiro.
 */
export function calculeFase(atual: number, concluido: boolean, pedidosReais: number, primeiroPedidoRealEm: Date | null, agora: Date): number {
  const base = Math.max(1, Number(atual || 1));
  if (!concluido) return base;
  let fase = 2;
  if (pedidosReais > 0) fase = 3;
  const seteDias = 7 * 24 * 60 * 60 * 1000;
  if (pedidosReais >= 10 || (primeiroPedidoRealEm && agora.getTime() - new Date(primeiroPedidoRealEm).getTime() >= seteDias)) fase = 4;
  return Math.max(base, fase);
}

/**
 * "Usa hoje" da ficha comercial indica outro sistema? Caderno, papel, WhatsApp e "nada" não contam:
 * quem só anota no papel não tem menu nenhum para comparar.
 */
export function ehExperiente(ferramentaAtual: string | null | undefined): boolean {
  const texto = String(ferramentaAtual || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
  if (!texto) return false;
  if (/^(nada|nenhum|nenhuma|nao|nao uso|sem sistema|so whatsapp|apenas whatsapp|whatsapp|zap|caderno|papel|comanda de papel|bloco|anotacao|anoto)\b/.test(texto)) return false;
  return true;
}
